;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { createServerIdempotencyHeaders } from '@workspace/request-idempotency';

export function formatNativeTeamsFeUserAgent(appShortName: string): string {
  return `NativeTeams-FE/${appShortName}`;
}

export function formatNativeTeamsFeUserAgentWithOriginal(
  appShortName: string,
  originalUserAgent?: string | null
): string {
  const base = formatNativeTeamsFeUserAgent(appShortName);
  const trimmedOriginalUserAgent = originalUserAgent?.trim();

  return trimmedOriginalUserAgent ? `${base} ${trimmedOriginalUserAgent}` : base;
}

/** Value for `User-Agent` segment; set `APP_NAME` per app deployment (e.g. auth-app). */
export function resolveOutgoingHttpAppName(): string {
  return process.env.APP_NAME?.trim() || 'unknown';
}

interface IncomingHeaderReader {
  get(name: string): string | null;
}

/** Resolve the client IP from inbound proxy/CDN headers. */
export function getClientIpFromIncomingHeaders(headers: IncomingHeaderReader): string | undefined {
  const forwardedFor = headers.get('x-forwarded-for');
  const clientIp =
    forwardedFor?.split(',')[0]?.trim() ||
    headers.get('x-real-ip') ||
    headers.get('cf-connecting-ip') ||
    undefined;

  return clientIp || undefined;
}

function headersInitToRecord(init?: HeadersInit): Record<string, string> {
  if (!init) return {};
  if (init instanceof Headers) {
    const out: Record<string, string> = {};
    init.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }
  if (Array.isArray(init)) {
    return Object.fromEntries(init);
  }
  return { ...init };
}

async function resolveOutgoingTraceId(): Promise<string> {
  try {
    const { headers: getHeaders } = await import('next/headers');
    const headersList = await getHeaders();
    const existing = headersList.get('x-trace-id')?.trim();
    if (existing) return existing;
  } catch {
    // Outside a request context (e.g. build) — generate a fresh id.
  }
  return crypto.randomUUID();
}

export async function getOutgoingXTraceIdHeader(): Promise<
  { 'X-Trace-Id': string } | Record<string, never>
> {
  if (typeof window !== 'undefined') return {};
  return { 'X-Trace-Id': await resolveOutgoingTraceId() };
}

interface MergeServerOutgoingFetchHeadersOptions {
  originalUserAgent?: string | null;
  appName?: string | null;
}

interface MergeNativeTeamsServerOutgoingFetchHeadersOptions
  extends MergeServerOutgoingFetchHeadersOptions {
  method?: string;
  inboundHeaders?: HeadersInit;
}

export async function mergeServerOutgoingFetchHeaders(
  base?: HeadersInit,
  options?: MergeServerOutgoingFetchHeadersOptions
): Promise<Record<string, string>> {
  if (typeof window !== 'undefined') {
    return headersInitToRecord(base);
  }

  const traceId = await resolveOutgoingTraceId();
  const appName = options?.appName?.trim() || resolveOutgoingHttpAppName();
  const outgoing: Record<string, string> = {
    'User-Agent': formatNativeTeamsFeUserAgentWithOriginal(appName, options?.originalUserAgent),
    'X-Trace-Id': traceId,
  };

  return { ...headersInitToRecord(base), ...outgoing };
}

export async function mergeNativeTeamsServerOutgoingFetchHeaders(
  base?: HeadersInit,
  options?: MergeNativeTeamsServerOutgoingFetchHeadersOptions
): Promise<Record<string, string>> {
  const headersWithIdempotency = createServerIdempotencyHeaders({
    method: options?.method,
    headers: base,
    inboundHeaders: options?.inboundHeaders,
  });

  return mergeServerOutgoingFetchHeaders(headersWithIdempotency, {
    originalUserAgent: options?.originalUserAgent,
    appName: options?.appName,
  });
}
